/**
 * Per-notification-type tab deep-link.
 *
 * Some notifications should open their entity's detail page on a specific tab
 * rather than the default one — e.g. a new ticket message lands on the Messages
 * tab, and a comment mention lands on the Comments tab so the comment that
 * triggered the alert is immediately visible.
 *
 * This is the single source of truth for that frontend mapping, shared by every
 * navigation surface (bell list, web toast/native handler, mobile push). It is
 * kept in sync with the backend email CTA equivalent,
 * `NotificationEmailService._apply_deep_link_tab` (shared/notifications/email_delivery.py).
 */

/** Tab key to deep-link to for a given notification type, or null for none. */
export function notificationDeepLinkTab(notificationType: string | undefined): string | null {
  switch (notificationType) {
    case 'ticket.message.new':
      return 'messages';
    case 'comment.mentioned':
    case 'comment.added':
      return 'comments';
    default:
      return null;
  }
}

/**
 * Set the deep-link `tab=` param for a notification type on an entity URL.
 * Returns the URL unchanged when the type has no tab mapping.
 *
 * The notification's tab REPLACES any `tab=` the entity template already carries
 * (a `urlContextTemplate` can pin one — e.g. a chatbot channel resolves to
 * `/chatbot/bots/{bot_id}?tab=channels`), rather than being appended alongside
 * it: two `tab=` params would leave the destination page picking whichever it
 * reads first. Other query params on the template are preserved.
 */
export function applyNotificationDeepLinkTab(
  url: string,
  notificationType: string | undefined,
): string {
  const tab = notificationDeepLinkTab(notificationType);
  if (!tab) return url;
  const [path, query] = url.split('?');
  const params = new URLSearchParams(query);
  params.set('tab', tab);
  return `${path}?${params.toString()}`;
}
